import {
    AlertCircleIcon,
    AlertTriangleIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronsDownUpIcon,
    ChevronsUpDownIcon,
    ExternalLinkIcon,
    InfoIcon,
    Loader2Icon,
    MinusIcon,
    PlayIcon,
    PlusIcon,
    XIcon,
} from "lucide-react";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Input} from "@components/ui/input";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    type LedgerFilters,
    type PnLCheckFigure,
    type PnLExplorerForecastParams,
    type PnLExplorerRow,
    type VNextOpInput,
    commitOps,
    runKPISnapshot,
    useVNextKPIRun,
    useVNextPnLExplorer,
} from "@hooks/use-vnext";

import {ProvenanceErrorBoundary} from "./ProvenanceErrorBoundary";
import {ProvenancePanel} from "./ProvenancePanel";

// ── Helpers ─────────────────────────────────────────────────────

function formatAmount(value: number | undefined, isPct?: boolean): string {
    if (value === undefined || value === null) return "—";
    if (isPct) return `${value.toFixed(1)}%`;
    const formatted = Math.abs(value).toLocaleString("en-AU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    return value < 0 ? `(${formatted})` : formatted;
}

function formatCurrency(value: number): string {
    return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2,
    }).format(value);
}

function formatDate(iso: string): string {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-AU", {day: "2-digit", month: "short", year: "numeric"});
}

function sourceTypeBadge(sourceType: string): "default" | "secondary" | "outline" {
    if (sourceType === "ACCREC" || sourceType === "CASHREC") return "default";
    if (sourceType === "ACCPAY" || sourceType === "CASHPAID") return "secondary";
    return "outline";
}

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Returns true if dateStr (YYYY-MM-DD) is more than 2 months before today. */
function isSnapshotStale(dateStr: string | undefined | null): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr + "T00:00:00");
    const now = new Date();
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
    return d < twoMonthsAgo;
}

/** Formats "2023-01-15" → "Jan 2023" */
function formatDateShort(dateStr: string | undefined | null): string {
    if (!dateStr) return "—";
    const m = parseInt(dateStr.slice(5, 7), 10);
    const y = dateStr.slice(0, 4);
    return `${SHORT_MONTHS[m - 1]} ${y}`;
}

function formatMonthKey(mk: string): string {
    const m = parseInt(mk.slice(5, 7), 10);
    const y = mk.slice(2, 4);
    return `${SHORT_MONTHS[m - 1]} ${y}`;
}

function lastDayOfMonth(monthKey: string): string {
    const [yyyy, mm] = monthKey.split("-").map(Number);
    const d = new Date(yyyy, mm, 0);
    return `${yyyy}-${String(mm).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Describes one visible column in the table. */
interface ColumnDef {
    id: string;
    label: string;
    subLabel?: string;
    quarter: string;
    type: "quarter" | "month";
    monthKey?: string;
    isForecast?: boolean;
}

/** Tracks which cell the user clicked to open the side panel. */
interface SelectedCell {
    label: string;
    value: number;
    monthKey?: string;
    quarter: string;
    level: string;
    filters: Record<string, string | undefined>;
    nodeId?: string;
    isForecast?: boolean;
    accountCode?: string;
}

/** Tracks which cell is being inline-edited. */
interface EditingCell {
    rowKey: string;
    monthKey: string;
    nodeId: string;
    label: string;
}

// Context to avoid prop-drilling the cell click handler through every level
const CellClickCtx = React.createContext<((cell: SelectedCell) => void) | null>(null);

// Context for inline editing
interface EditCtxValue {
    editingCell: EditingCell | null;
    editValue: string;
    setEditValue: (v: string) => void;
    startEdit: (cell: EditingCell, currentValue: number | undefined) => void;
    commitEdit: () => void;
    cancelEdit: () => void;
    monthStatus: Record<string, "actual" | "forecast">;
}
const EditCtx = React.createContext<EditCtxValue | null>(null);

// ── Value cells — renders the correct columns for a row ──────

interface ValueCellsProps {
    row: PnLExplorerRow;
    columns: ColumnDef[];
    level: string;
    filters: Record<string, string | undefined>;
    clickable?: boolean;
}

function ValueCells({row, columns, level, filters, clickable}: ValueCellsProps) {
    const onCellClick = React.useContext(CellClickCtx);
    const editCtx = React.useContext(EditCtx);
    const isPct = row.is_pct;

    return (
        <>
            {columns.map((col) => {
                const value =
                    col.type === "quarter"
                        ? row.quarters?.[col.quarter]
                        : row.months?.[col.monthKey!];
                const isForecastCell = col.isForecast;
                const hasForecastDetailTarget = !isForecastCell || Boolean(row.node_id);
                const canClick = clickable && onCellClick && value !== undefined && !isPct && hasForecastDetailTarget;

                // Check if this cell is being inline-edited
                const isEditing =
                    editCtx?.editingCell &&
                    editCtx.editingCell.rowKey === row.key &&
                    col.monthKey &&
                    editCtx.editingCell.monthKey === col.monthKey;

                // Can this cell be double-clicked to edit?
                const canEdit =
                    isForecastCell &&
                    col.type === "month" &&
                    row.node_id &&
                    !row.is_computed &&
                    !isPct &&
                    editCtx;

                if (isEditing && editCtx) {
                    return (
                        <TableCell key={col.id} className="p-0.5 bg-blue-50/50">
                            <Input
                                autoFocus
                                className="h-7 text-right text-sm tabular-nums w-[90px]"
                                value={editCtx.editValue}
                                onChange={(e) => editCtx.setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") editCtx.commitEdit();
                                    if (e.key === "Escape") editCtx.cancelEdit();
                                }}
                                onBlur={() => editCtx.commitEdit()}
                            />
                        </TableCell>
                    );
                }

                return (
                    <TableCell
                        key={col.id}
                        className={`text-right tabular-nums whitespace-nowrap ${isPct ? "text-muted-foreground italic text-xs" : ""} ${canClick ? "cursor-pointer hover:bg-muted/40" : ""} ${isForecastCell && !isPct ? "bg-blue-50/50 text-blue-900" : ""}`}
                        onClick={
                            canClick
                                ? () =>
                                      onCellClick({
                                          label: row.label,
                                          value: value ?? 0,
                                          monthKey: col.monthKey,
                                          quarter: col.quarter,
                                          level,
                                          filters,
                                          nodeId: row.node_id,
                                          isForecast: isForecastCell,
                                          accountCode: row.account_code,
                                      })
                                : undefined
                        }
                        onDoubleClick={
                            canEdit
                                ? (e) => {
                                      e.stopPropagation();
                                      editCtx.startEdit(
                                          {
                                              rowKey: row.key,
                                              monthKey: col.monthKey!,
                                              nodeId: row.node_id!,
                                              label: row.label,
                                          },
                                          value,
                                      );
                                  }
                                : undefined
                        }
                    >
                        {formatAmount(value, isPct)}
                    </TableCell>
                );
            })}
        </>
    );
}

// ── Explorer detail panel (side panel for clicked actual cell) ──────────

interface ExplorerDetailPanelProps {
    workspaceId: string;
    runId: string;
    cell: SelectedCell;
    quarterMonthKeys: Record<string, string[]>;
    onClose: () => void;
}

function ExplorerDetailPanel({workspaceId, runId, cell, quarterMonthKeys, onClose}: ExplorerDetailPanelProps) {
    // Build transaction filters from the cell context
    const txnFilters = useMemo(() => {
        const f: Record<string, string | undefined> = {...cell.filters};
        if (cell.monthKey) {
            f.month_key = cell.monthKey;
        }
        return f;
    }, [cell.filters, cell.monthKey]);

    const {data, isLoading} = useVNextPnLExplorer(workspaceId, runId, "transaction", txnFilters);
    const rows = data?.rows ?? [];
    const total = data?.total ?? 0;

    const monthLabel = cell.monthKey ? formatMonthKey(cell.monthKey) : cell.quarter;

    const handleOpenTransactions = useCallback(() => {
        const filters: LedgerFilters = {
            page: 1,
            page_size: 100,
            sort: "-date",
        };
        if (cell.monthKey) {
            filters.date_from = `${cell.monthKey}-01`;
            filters.date_to = lastDayOfMonth(cell.monthKey);
        } else {
            // Quarter — use first/last month in quarter
            const months = quarterMonthKeys[cell.quarter] ?? [];
            const sorted = [...months].sort();
            if (sorted.length > 0) {
                filters.date_from = `${sorted[0]}-01`;
                filters.date_to = lastDayOfMonth(sorted[sorted.length - 1]);
            }
        }
        // Pass account_id if available in filters
        if (cell.filters.account_id) {
            filters.account_id = cell.filters.account_id;
        }
        window.dispatchEvent(new CustomEvent("vnext-open-transactions", {detail: {filters}}));
        onClose();
    }, [cell, quarterMonthKeys, onClose]);

    return (
        <div className="fixed right-0 top-0 z-50 flex h-full w-[380px] flex-col border-l bg-background shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between border-b p-4">
                <div className="flex items-center gap-2">
                    <InfoIcon className="size-4 text-[#209DB3]" />
                    <h3 className="font-semibold text-sm">Cell Detail</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose}>
                    <XIcon className="size-4" />
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Cell info */}
                <div className="rounded-md border p-3 space-y-1">
                    <p className="text-sm font-medium">{cell.label}</p>
                    <p className="text-xs text-muted-foreground">{monthLabel}</p>
                    <p className="text-2xl font-bold tabular-nums">
                        {formatCurrency(cell.value)}
                    </p>
                </div>

                {/* Loading */}
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
                    </div>
                )}

                {/* Transactions */}
                {!isLoading && rows.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Transactions ({rows.length}{total > rows.length ? ` of ${total}` : ""})
                        </h4>
                        <div className="space-y-1 max-h-[400px] overflow-y-auto">
                            {rows.map((txn: PnLExplorerRow) => (
                                <div key={txn.key} className="rounded border p-2 text-xs">
                                    <div className="flex justify-between">
                                        <span className="font-medium truncate mr-2">
                                            {txn.description || "Transaction"}
                                        </span>
                                        <span className="tabular-nums font-medium whitespace-nowrap">
                                            {txn.net_amount !== undefined ? formatCurrency(txn.net_amount) : "—"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>{txn.date ? formatDate(txn.date) : "—"}</span>
                                        <span className="flex items-center gap-1">
                                            {txn.source_type && (
                                                <Badge variant={sourceTypeBadge(txn.source_type)} className="text-[10px] px-1 py-0">
                                                    {txn.source_type}
                                                </Badge>
                                            )}
                                            {txn.contact_name || txn.account_code || ""}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {total > rows.length && (
                            <p className="text-xs text-muted-foreground text-center">
                                Showing {rows.length} of {total} transactions
                            </p>
                        )}
                    </div>
                )}

                {!isLoading && rows.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">
                        No transactions found for this cell.
                    </p>
                )}

                {/* Open in Transactions */}
                <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    onClick={handleOpenTransactions}
                >
                    <ExternalLinkIcon className="size-3 mr-1.5" />
                    Open in Transactions
                </Button>
            </div>
        </div>
    );
}

// ── Sub-row component (each expanded level uses its own SWR hook) ──
// forecastParams are threaded through so account-level children also
// fetch engine forecast data. The backend only merges forecast at
// section/account levels — deeper levels (segment/customer)
// harmlessly ignore the params and remain actuals-only.
// Transactions are NOT rendered inline — they appear in the side panel
// (ExplorerDetailPanel) or via the Transactions page.

interface ExpandedChildrenProps {
    workspaceId: string;
    runId: string;
    level: string;
    filters: Record<string, string | undefined>;
    columns: ColumnDef[];
    depth: number;
    forecastParams?: PnLExplorerForecastParams;
}

function ExpandedChildren({workspaceId, runId, level, filters, columns, depth, forecastParams}: ExpandedChildrenProps) {
    const {data, isLoading, error} = useVNextPnLExplorer(workspaceId, runId, level, filters, forecastParams);

    if (isLoading) {
        return (
            <TableRow>
                <TableCell colSpan={columns.length + 2}>
                    <div className="flex items-center gap-2" style={{paddingLeft: `${depth * 24 + 16}px`}}>
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-20" />
                    </div>
                </TableCell>
            </TableRow>
        );
    }

    if (error) {
        return (
            <TableRow>
                <TableCell colSpan={columns.length + 2}>
                    <div className="text-xs text-destructive" style={{paddingLeft: `${depth * 24 + 16}px`}}>
                        Failed to load: {error}
                    </div>
                </TableCell>
            </TableRow>
        );
    }

    if (!data?.rows?.length) return null;

    return (
        <>
            {data.rows.map((row: PnLExplorerRow) => (
                <ExplorerRow
                    key={row.key}
                    row={row}
                    columns={columns}
                    depth={depth}
                    workspaceId={workspaceId}
                    runId={runId}
                    level={level}
                    filters={filters}
                    forecastParams={forecastParams}
                />
            ))}
        </>
    );
}

// ── Single row with expand/collapse ─────────────────────────────

const EXPENSE_SECTIONS = new Set(["expenses", "cogs"]);

function getNextLevel(level: string, pnlSection?: string): string | undefined {
    if (level === "section") {
        return pnlSection && EXPENSE_SECTIONS.has(pnlSection) ? "parent_group" : "account";
    }
    if (level === "parent_group") return "account_group";
    if (level === "account_group") return "account";
    if (level === "account") {
        return pnlSection && EXPENSE_SECTIONS.has(pnlSection) ? "supplier" : "segment";
    }
    if (level === "segment") return "customer_parent";
    if (level === "customer_parent") return "customer_child";
    return undefined; // leaf levels: customer_child, supplier
}

const FILTER_KEY_FOR_LEVEL: Record<string, string> = {
    section: "pnl_section",
    parent_group: "parent_group",
    account_group: "account_group",
    account: "account_id",
    segment: "segment",
    customer_parent: "parent_contact_id",
    customer_child: "contact_id",
    supplier: "contact_id",
};

interface ExplorerRowProps {
    row: PnLExplorerRow;
    columns: ColumnDef[];
    depth: number;
    workspaceId: string;
    runId: string;
    level: string;
    filters: Record<string, string | undefined>;
    forecastParams?: PnLExplorerForecastParams;
}

function ExplorerRow({row, columns, depth, workspaceId, runId, level, filters, forecastParams}: ExplorerRowProps) {
    const [expanded, setExpanded] = useState(false);

    const isComputed = row.is_computed;
    // Determine pnlSection from filters or from the row key when at section level
    const pnlSection = level === "section" ? row.key : filters.pnl_section;
    const nextLevel = getNextLevel(level, pnlSection);
    const isExpandable = row.is_expandable && !isComputed && !!nextLevel;
    const filterKey = FILTER_KEY_FOR_LEVEL[level];

    const handleToggle = useCallback(() => {
        if (isExpandable) setExpanded((prev) => !prev);
    }, [isExpandable]);

    const childFilters = useMemo(
        () => ({
            ...filters,
            ...(filterKey ? {[filterKey]: row.key} : {}),
        }),
        [filters, filterKey, row.key],
    );

    const paddingLeft = depth * 24 + 16;

    const isPct = row.is_pct;
    const rowClasses = [
        isComputed && !isPct ? "font-semibold border-t border-border" : "",
        isPct ? "border-b border-border/50" : "",
        depth === 0 && !isComputed && !isPct ? "bg-muted/30 font-medium" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <>
            <TableRow className={rowClasses}>
                <TableCell
                    className={`sticky left-0 bg-background z-10 whitespace-nowrap ${isPct ? "text-muted-foreground italic text-xs" : "cursor-pointer"}`}
                    style={{paddingLeft: `${paddingLeft}px`}}
                    onClick={isPct ? undefined : handleToggle}
                >
                    <span className="flex items-center gap-1">
                        {isExpandable ? (
                            expanded ? (
                                <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                            ) : (
                                <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                            )
                        ) : (
                            <span className="w-4" />
                        )}
                        <span className={depth >= 3 ? "text-sm" : ""}>{row.label}</span>
                        {row.expansion_blocked && (
                            <span className="text-[10px] text-amber-600 bg-amber-50 px-1 rounded" title={`Drilldown blocked: ${row.expansion_blocked}`}>
                                no account link
                            </span>
                        )}
                    </span>
                </TableCell>
                <ValueCells
                    row={row}
                    columns={columns}
                    level={level}
                    filters={childFilters}
                    clickable={!isComputed}
                />
                <TableCell className={`sticky right-0 bg-background z-10 text-right tabular-nums whitespace-nowrap font-medium border-l ${isPct ? "text-muted-foreground italic text-xs" : ""}`}>
                    {formatAmount(row.total, isPct)}
                </TableCell>
            </TableRow>
            {expanded && nextLevel && (
                <ExpandedChildren
                    workspaceId={workspaceId}
                    runId={runId}
                    level={nextLevel}
                    filters={childFilters}
                    columns={columns}
                    depth={depth + 1}
                    forecastParams={forecastParams}
                />
            )}
        </>
    );
}

// ── Check figures footer (variance to P&L) ──────────────────────

function formatVariance(value: number | undefined): string {
    if (value === undefined || value === null || value === 0) return "—";
    const formatted = Math.abs(value).toLocaleString("en-AU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    return value < 0 ? `(${formatted})` : formatted;
}

function CheckFigures({
    figures,
    columns,
}: {
    figures: PnLCheckFigure[];
    columns: ColumnDef[];
}) {
    const [open, setOpen] = useState(false);

    if (!figures?.length) return null;

    const allZero = figures.every((f) => f.total === 0);

    return (
        <tfoot className="border-t-2 border-border">
            <TableRow className="bg-muted/40 cursor-pointer select-none" onClick={() => setOpen(!open)}>
                <TableCell colSpan={columns.length + 2} className="sticky left-0 bg-muted z-20 py-1">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide inline-flex items-center gap-1">
                        {open ? <ChevronDownIcon className="size-3.5" /> : <ChevronRightIcon className="size-3.5" />}
                        Xero Variance Check{allZero ? " — Tied" : ""}
                    </span>
                </TableCell>
            </TableRow>
            {open && figures.map((fig) => {
                const hasDebug = fig.model_months && fig.xero_months;
                return (
                    <React.Fragment key={fig.key}>
                        {/* Model row */}
                        {hasDebug && (
                            <TableRow className="bg-muted/20">
                                <TableCell className="sticky left-0 bg-muted z-20 text-xs whitespace-nowrap py-0.5 pl-6 text-muted-foreground">
                                    {fig.label} (Model)
                                </TableCell>
                                {columns.map((col) => {
                                    const value =
                                        col.type === "quarter"
                                            ? fig.model_quarters?.[col.quarter]
                                            : fig.model_months?.[col.monthKey!];
                                    return (
                                        <TableCell
                                            key={col.id}
                                            className="text-right tabular-nums whitespace-nowrap text-xs py-0.5 text-muted-foreground"
                                        >
                                            {value !== undefined ? formatAmount(value) : "—"}
                                        </TableCell>
                                    );
                                })}
                                <TableCell className="sticky right-0 bg-muted z-20 text-right tabular-nums whitespace-nowrap text-xs py-0.5 border-l text-muted-foreground">
                                    {fig.model_total !== undefined ? formatAmount(fig.model_total) : "—"}
                                </TableCell>
                            </TableRow>
                        )}
                        {/* Xero row */}
                        {hasDebug && (
                            <TableRow className="bg-muted/20">
                                <TableCell className="sticky left-0 bg-muted z-20 text-xs whitespace-nowrap py-0.5 pl-6 text-muted-foreground">
                                    {fig.label} (Xero)
                                </TableCell>
                                {columns.map((col) => {
                                    const value =
                                        col.type === "quarter"
                                            ? fig.xero_quarters?.[col.quarter]
                                            : fig.xero_months?.[col.monthKey!];
                                    return (
                                        <TableCell
                                            key={col.id}
                                            className="text-right tabular-nums whitespace-nowrap text-xs py-0.5 text-muted-foreground"
                                        >
                                            {value !== undefined ? formatAmount(value) : "—"}
                                        </TableCell>
                                    );
                                })}
                                <TableCell className="sticky right-0 bg-muted z-20 text-right tabular-nums whitespace-nowrap text-xs py-0.5 border-l text-muted-foreground">
                                    {fig.xero_total !== undefined ? formatAmount(fig.xero_total) : "—"}
                                </TableCell>
                            </TableRow>
                        )}
                        {/* Variance row */}
                        <TableRow className={`${fig.is_computed ? "font-semibold border-t" : ""} bg-muted/40`}>
                            <TableCell className="sticky left-0 bg-muted z-20 text-xs whitespace-nowrap py-1">
                                {fig.label}{hasDebug ? " (Var)" : ""}
                            </TableCell>
                            {columns.map((col) => {
                                const value =
                                    col.type === "quarter"
                                        ? fig.quarters?.[col.quarter]
                                        : fig.months?.[col.monthKey!];
                                const isMissing = value === undefined || value === null;
                                return (
                                    <TableCell
                                        key={col.id}
                                        className={`text-right tabular-nums whitespace-nowrap text-xs py-1 ${
                                            isMissing
                                                ? "text-muted-foreground/40"
                                                : value !== 0
                                                    ? "text-destructive font-medium"
                                                    : "text-emerald-600"
                                        }`}
                                    >
                                        {isMissing ? "—" : formatVariance(value)}
                                    </TableCell>
                                );
                            })}
                            <TableCell
                                className={`sticky right-0 bg-muted z-20 text-right tabular-nums whitespace-nowrap text-xs font-semibold py-1 border-l ${
                                    fig.total !== 0 ? "text-destructive" : "text-emerald-600"
                                }`}
                            >
                                {formatVariance(fig.total)}
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                );
            })}
        </tfoot>
    );
}

// ── Main PnLExplorerTab ─────────────────────────────────────────

interface PnLExplorerTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
    fyStartMonth?: number;
    // Accepted from WorkspaceShell but not used — Explorer is a rolling trend table
    currentMonth?: string;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    compare?: string;
}

export function PnLExplorerTab({
    workspaceId,
    scenarioId,
    actualMonths = 12,
    forecastMonths = 6,
    fyStartMonth,
}: PnLExplorerTabProps) {
    const [activeRunId, setActiveRunId] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [expandedQuarters, setExpandedQuarters] = useState<Set<string>>(new Set());
    const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

    // Inline editing state
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [editValue, setEditValue] = useState("");
    const [committing, setCommitting] = useState(false);
    const commitAbort = useRef<AbortController | null>(null);

    // Poll active run status
    const {data: runStatus} = useVNextKPIRun(
        workspaceId,
        activeRunId && creating ? activeRunId : null,
    );

    // When run completes, stop polling and use it.
    // Only forward activeRunId while creating — once done, let the backend
    // resolve the latest run so we don't send a stale/expired run_id.
    const effectiveRunId =
        runStatus?.status === "done" ? runStatus.run_id : creating && activeRunId ? activeRunId : undefined;

    // Build forecast params for top-level fetch
    const forecastParams: PnLExplorerForecastParams | undefined = useMemo(() => ({
        scenarioId,
        forecastMonths,
        actualMonths,
        fyStartMonth,
    }), [scenarioId, forecastMonths, actualMonths, fyStartMonth]);

    // Fetch section-level data (default view) — with forecast
    const {data: explorerData, isLoading, error, refresh} = useVNextPnLExplorer(
        workspaceId,
        effectiveRunId ?? null,
        "section",
        {},
        forecastParams,
    );

    // Month status map
    const monthStatus = useMemo<Record<string, "actual" | "forecast">>(
        () => explorerData?.month_status ?? {},
        [explorerData?.month_status],
    );

    // Handle run completion
    React.useEffect(() => {
        if (runStatus?.status === "done" && creating) {
            setCreating(false);
            setActiveRunId(runStatus.run_id);
            refresh();
        } else if (runStatus?.status === "error" && creating) {
            setCreating(false);
            setCreateError(runStatus.error_message || "Snapshot failed");
        }
    }, [runStatus, creating, refresh]);

    const handleCreateSnapshot = useCallback(async () => {
        setCreating(true);
        setCreateError(null);
        try {
            const result = await runKPISnapshot(workspaceId, scenarioId);
            setActiveRunId(result.run_id);
        } catch (e) {
            setCreating(false);
            setCreateError(e instanceof Error ? e.message : "Failed to create snapshot");
        }
    }, [workspaceId, scenarioId]);

    const handleCellClick = useCallback((cell: SelectedCell) => {
        setSelectedCell(cell);
    }, []);

    // ── Inline editing handlers ──

    const startEdit = useCallback((cell: EditingCell, currentValue: number | undefined) => {
        setEditingCell(cell);
        setEditValue(currentValue !== undefined ? String(currentValue) : "");
        setSelectedCell(null); // close side panel if open
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingCell(null);
        setEditValue("");
    }, []);

    const commitEdit = useCallback(async () => {
        if (!editingCell || committing) return;

        const parsed = parseFloat(editValue);
        if (isNaN(parsed)) {
            cancelEdit();
            return;
        }

        setCommitting(true);
        commitAbort.current?.abort();
        const ctrl = new AbortController();
        commitAbort.current = ctrl;

        try {
            const ops: VNextOpInput[] = [
                {
                    op_type: "set_override",
                    target_node_id: editingCell.nodeId,
                    target_month: editingCell.monthKey,
                    label: `Override ${editingCell.label} ${formatMonthKey(editingCell.monthKey)}`,
                    params: {value: parsed},
                },
            ];
            await commitOps(workspaceId, ops, scenarioId, forecastMonths, "pnl", "human", actualMonths);
            setEditingCell(null);
            setEditValue("");
            refresh();
        } catch {
            // Leave editing state so user can retry
        } finally {
            setCommitting(false);
        }
    }, [editingCell, editValue, committing, cancelEdit, workspaceId, scenarioId, forecastMonths, actualMonths, refresh]);

    // Edit context value
    const editCtxValue = useMemo<EditCtxValue>(
        () => ({editingCell, editValue, setEditValue, startEdit, commitEdit, cancelEdit, monthStatus}),
        [editingCell, editValue, startEdit, commitEdit, cancelEdit, monthStatus],
    );

    const data = explorerData;
    const quarters = useMemo(() => data?.quarters ?? [], [data?.quarters]);
    const quarterMonthKeys = useMemo(() => data?.quarter_month_keys ?? {}, [data?.quarter_month_keys]);
    const rows: PnLExplorerRow[] = data?.rows ?? [];
    const runId = data?.run_id;

    // Expand all quarters by default when data first loads
    useEffect(() => {
        if (quarters.length > 0) {
            setExpandedQuarters(new Set(quarters));
        }
    }, [quarters]);

    // Toggle a single quarter
    const toggleQuarter = useCallback((q: string) => {
        setExpandedQuarters((prev) => {
            const next = new Set(prev);
            if (next.has(q)) next.delete(q);
            else next.add(q);
            return next;
        });
    }, []);

    // Expand / collapse all
    const allExpanded = quarters.length > 0 && expandedQuarters.size === quarters.length;
    const toggleAll = useCallback(() => {
        if (allExpanded) {
            setExpandedQuarters(new Set());
        } else {
            setExpandedQuarters(new Set(quarters));
        }
    }, [allExpanded, quarters]);

    // Build flat column list based on which quarters are expanded
    const columns: ColumnDef[] = useMemo(() => {
        const cols: ColumnDef[] = [];
        for (const q of quarters) {
            const monthKeys = quarterMonthKeys[q] ?? [];
            if (expandedQuarters.has(q)) {
                for (const mk of [...monthKeys].sort()) {
                    cols.push({
                        id: `month-${mk}`,
                        label: formatMonthKey(mk),
                        quarter: q,
                        type: "month",
                        monthKey: mk,
                        isForecast: monthStatus[mk] === "forecast",
                    });
                }
            } else {
                const sorted = [...monthKeys].sort();
                const m1 = parseInt(sorted[0]?.slice(5, 7) ?? "0", 10);
                const m2 = parseInt(sorted[sorted.length - 1]?.slice(5, 7) ?? "0", 10);
                const subLabel = m1 && m2 ? `${SHORT_MONTHS[m1 - 1]}–${SHORT_MONTHS[m2 - 1]}` : "";
                // A quarter is forecast if ALL months in it are forecast
                const allForecast = sorted.length > 0 && sorted.every((mk) => monthStatus[mk] === "forecast");
                cols.push({
                    id: `quarter-${q}`,
                    label: q,
                    subLabel,
                    quarter: q,
                    type: "quarter",
                    isForecast: allForecast,
                });
            }
        }
        return cols;
    }, [quarters, quarterMonthKeys, expandedQuarters, monthStatus]);

    // Determine if the selected cell is a forecast cell
    const selectedCellIsForecast = selectedCell?.isForecast ?? false;

    return (
        <CellClickCtx.Provider value={handleCellClick}>
            <EditCtx.Provider value={editCtxValue}>
                <div className="flex flex-col flex-1 min-h-0 gap-4">
                    {/* Header */}
                    <div className="flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold">P&amp;L Explorer</h2>
                            {data?.snapshot_info?.date_from && data?.snapshot_info?.date_to && (
                                <span className="text-xs text-muted-foreground">
                                    {formatDateShort(data.snapshot_info.date_from)} – {formatDateShort(data.snapshot_info.date_to)}
                                </span>
                            )}
                            {data?.is_final === true && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Badge variant="default" className="gap-1">
                                                <CheckCircleIcon className="h-3 w-3" />
                                                Final
                                            </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>All completeness gates passed</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                            {data?.is_final === false && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Badge variant="secondary" className="gap-1">
                                                <AlertCircleIcon className="h-3 w-3" />
                                                Draft
                                            </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>Some completeness checks did not pass</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {committing && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Loader2Icon className="h-3 w-3 animate-spin" />
                                    Saving...
                                </span>
                            )}
                            {runId && quarters.length > 0 && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="outline" size="sm" onClick={toggleAll}>
                                                {allExpanded ? (
                                                    <ChevronsDownUpIcon className="h-4 w-4" />
                                                ) : (
                                                    <ChevronsUpDownIcon className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {allExpanded ? "Group all into quarters" : "Expand all to months"}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                            <Button onClick={handleCreateSnapshot} disabled={creating} size="sm">
                                {creating ? (
                                    <>
                                        <Loader2Icon className="h-4 w-4 mr-1 animate-spin" />
                                        {runStatus?.status === "running" ? "Building snapshot..." : "Starting..."}
                                    </>
                                ) : (
                                    <>
                                        <PlayIcon className="h-4 w-4 mr-1" />
                                        Rebuild Snapshot
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {createError && (
                        <div className="text-sm text-destructive bg-destructive/10 rounded p-2 shrink-0">{createError}</div>
                    )}

                    {/* Snapshot diagnostics — collapsed by default */}
                    {data?.snapshot_info && (
                        <details className="text-xs text-muted-foreground bg-muted/30 rounded-md shrink-0">
                            <summary className="cursor-pointer px-3 py-2 hover:bg-muted/50 select-none">
                                Snapshot: {data.snapshot_info.facts_count?.toLocaleString() ?? 0} facts · {data.snapshot_info.date_from ?? "—"} to {data.snapshot_info.date_to ?? "—"}
                                {data.snapshot_info.completeness_notes?.length > 0 && (
                                    <span className="text-amber-600 ml-2">
                                        ({data.snapshot_info.completeness_notes.length} note{data.snapshot_info.completeness_notes.length !== 1 ? "s" : ""})
                                    </span>
                                )}
                            </summary>
                            <div className="px-3 pb-3 space-y-1">
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    <span><strong>Facts:</strong> {data.snapshot_info.facts_count?.toLocaleString() ?? "—"}</span>
                                    <span><strong>Rollups:</strong> {data.snapshot_info.rollups_count?.toLocaleString() ?? "—"}</span>
                                    <span><strong>Period:</strong> {data.snapshot_info.date_from ?? "—"} to {data.snapshot_info.date_to ?? "—"}</span>
                                    <span><strong>Accounts:</strong> {data.snapshot_info.distinct_accounts?.length ?? 0}</span>
                                    <span><strong>Months:</strong> {data.snapshot_info.distinct_months?.length ?? 0}</span>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    <span><strong>Facts with contact:</strong> {data.snapshot_info.facts_with_contact_id?.toLocaleString() ?? "—"} / {data.snapshot_info.facts_count?.toLocaleString() ?? "—"}</span>
                                    <span><strong>Customer parent rollups:</strong> {data.snapshot_info.customer_parent_rollups?.toLocaleString() ?? "—"}</span>
                                </div>
                                {data.snapshot_info.distinct_accounts?.length > 0 && (
                                    <div className="text-[11px] text-muted-foreground/70">
                                        <strong>Account names:</strong> {data.snapshot_info.distinct_accounts.join(", ")}
                                    </div>
                                )}
                                {data.snapshot_info.completeness_notes?.length > 0 && (
                                    <div className="text-[11px] text-amber-600">
                                        <strong>Notes:</strong> {data.snapshot_info.completeness_notes.join("; ")}
                                    </div>
                                )}
                            </div>
                        </details>
                    )}

                    {/* Forecast diagnostics — shows mismatches between engine nodes and snapshot accounts */}
                    {data?.forecast_diagnostics && (data.forecast_diagnostics.orphan_forecasts.length > 0 || data.forecast_diagnostics.missing_forecasts.length > 0) && (
                        <details className="text-xs text-muted-foreground bg-amber-50/50 border border-amber-200 rounded-md shrink-0">
                            <summary className="cursor-pointer px-3 py-2 hover:bg-amber-50 select-none text-amber-800">
                                Forecast alignment: {data.forecast_diagnostics.matched} matched, {data.forecast_diagnostics.orphan_forecasts.length} orphaned, {data.forecast_diagnostics.missing_forecasts.length} missing
                            </summary>
                            <div className="px-3 pb-3 space-y-2">
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-amber-800">
                                    <span><strong>Engine nodes:</strong> {data.forecast_diagnostics.engine_node_count}</span>
                                    <span><strong>Snapshot accounts:</strong> {data.forecast_diagnostics.snapshot_account_count}</span>
                                    <span><strong>Matched:</strong> {data.forecast_diagnostics.matched}</span>
                                    <span><strong>Run:</strong> {data.source_mode ?? "—"} / {data.run_id?.slice(0, 8) ?? "—"}</span>
                                </div>
                                {data.forecast_diagnostics.orphan_forecasts.length > 0 && (
                                    <div>
                                        <div className="font-medium text-amber-700 mb-1">Forecast with no actuals (orphaned engine nodes):</div>
                                        {data.forecast_diagnostics.orphan_forecasts.map((o) => (
                                            <div key={o.xero_account_id} className="text-[11px] text-amber-800 pl-2">
                                                {o.label} ({o.section}) — forecast total: {o.forecast_total.toLocaleString()} — xero_id: {o.xero_account_id.slice(0, 12)}…
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {data.forecast_diagnostics.missing_forecasts.length > 0 && (
                                    <div>
                                        <div className="font-medium text-amber-700 mb-1">Actuals with no forecast (no matching engine node):</div>
                                        {data.forecast_diagnostics.missing_forecasts.map((m) => (
                                            <div key={m.account_id} className="text-[11px] text-amber-800 pl-2">
                                                {m.account_name} — actual total: {m.actual_total.toLocaleString()} — account_id: {m.account_id.slice(0, 12)}…
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </details>
                    )}

                    {/* Drilldown warnings (empty rollups, wrong source_mode, etc.) */}
                    {data?.drilldown_warnings && data.drilldown_warnings.length > 0 && (
                        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 shrink-0 space-y-1">
                            {data.drilldown_warnings.map((w, i) => (
                                <div key={i} className="flex items-start gap-2">
                                    <AlertTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                                    <span>{w}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Staleness warning */}
                    {data?.snapshot_info && isSnapshotStale(data.snapshot_info.date_to) && (
                        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 shrink-0">
                            <AlertTriangleIcon className="h-4 w-4 shrink-0" />
                            <span>
                                Snapshot data ends at {formatDateShort(data.snapshot_info.date_to)}. Data may be outdated.
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                className="ml-auto shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100"
                                onClick={handleCreateSnapshot}
                                disabled={creating}
                            >
                                {creating ? (
                                    <Loader2Icon className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                    <PlayIcon className="h-3 w-3 mr-1" />
                                )}
                                Rebuild Snapshot
                            </Button>
                        </div>
                    )}

                    {/* No data state */}
                    {!isLoading && !runId && (
                        <div className="text-center py-12 text-muted-foreground">
                            <p className="text-lg font-medium mb-2">No snapshot available</p>
                            <p className="text-sm">Click &ldquo;Sync from Xero&rdquo; to sync data and auto-build your P&amp;L snapshot.</p>
                        </div>
                    )}

                    {/* Loading state */}
                    {isLoading && (
                        <div className="space-y-2">
                            {[...Array(6)].map((_, i) => (
                                <Skeleton key={i} className="h-10 w-full" />
                            ))}
                        </div>
                    )}

                    {/* Error state */}
                    {error && <div className="text-sm text-destructive">{error}</div>}

                    {/* Tree grid table */}
                    {runId && rows.length > 0 && (
                        <div className="border rounded-lg overflow-auto flex-1 min-h-0 max-h-[calc(100vh-220px)]">
                            <Table style={{minWidth: `${260 + columns.length * 100 + 120}px`}}>
                                <TableHeader className="sticky top-0 bg-background z-30">
                                    <TableRow>
                                        <TableHead className="sticky left-0 bg-background z-30 min-w-[250px]">
                                            P&amp;L Section
                                        </TableHead>
                                        {columns.map((col) => (
                                            <TableHead
                                                key={col.id}
                                                className={`text-right whitespace-nowrap min-w-[90px] select-none ${col.isForecast ? "bg-blue-50 text-blue-900" : ""}`}
                                            >
                                                <div
                                                    className="flex items-center justify-end gap-1 cursor-pointer"
                                                    onClick={() => toggleQuarter(col.quarter)}
                                                >
                                                    {col.type === "quarter" ? (
                                                        <PlusIcon className="h-3 w-3 text-muted-foreground shrink-0" />
                                                    ) : (
                                                        columns.find((c) => c.quarter === col.quarter)?.id === col.id && (
                                                            <MinusIcon className="h-3 w-3 text-muted-foreground shrink-0" />
                                                        )
                                                    )}
                                                    <span>{col.label}</span>
                                                    {col.isForecast && (
                                                        <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px] font-medium text-blue-600 border-blue-300">
                                                            F
                                                        </Badge>
                                                    )}
                                                </div>
                                                {col.subLabel && (
                                                    <div className="text-xs font-normal text-muted-foreground">
                                                        {col.subLabel}
                                                    </div>
                                                )}
                                            </TableHead>
                                        ))}
                                        <TableHead className="sticky right-0 bg-background z-30 text-right whitespace-nowrap min-w-[110px] font-semibold border-l">
                                            Annual Total
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((row) => (
                                        <ExplorerRow
                                            key={row.key}
                                            row={row}
                                            columns={columns}
                                            depth={0}
                                            workspaceId={workspaceId}
                                            runId={runId}
                                            level="section"
                                            filters={{}}
                                            forecastParams={forecastParams}
                                        />
                                    ))}
                                </TableBody>
                                <CheckFigures
                                    figures={data?.xero_check_figures ?? []}
                                    columns={columns}
                                />
                            </Table>
                        </div>
                    )}
                </div>

                {/* Side panel for clicked cell */}
                {selectedCell && runId && !selectedCellIsForecast && (
                    <ExplorerDetailPanel
                        workspaceId={workspaceId}
                        runId={runId}
                        cell={selectedCell}
                        quarterMonthKeys={quarterMonthKeys}
                        onClose={() => setSelectedCell(null)}
                    />
                )}

                {/* Provenance panel for forecast cells */}
                {selectedCell && selectedCellIsForecast && selectedCell.nodeId && selectedCell.monthKey && (
                    <ProvenanceErrorBoundary onClose={() => setSelectedCell(null)}>
                        <ProvenancePanel
                            workspaceId={workspaceId}
                            scenarioId={scenarioId}
                            nodeId={selectedCell.nodeId}
                            nodeLabel={selectedCell.label}
                            monthKey={selectedCell.monthKey}
                            monthLabel={selectedCell.monthKey ? formatMonthKey(selectedCell.monthKey) : selectedCell.quarter}
                            value={selectedCell.value}
                            isForecast={true}
                            onClose={() => setSelectedCell(null)}
                            onOpenTransactions={(filters: LedgerFilters) => {
                                window.dispatchEvent(new CustomEvent("vnext-open-transactions", {detail: {filters}}));
                            }}
                            source="pnl"
                            accountCode={selectedCell.accountCode}
                        />
                    </ProvenanceErrorBoundary>
                )}
            </EditCtx.Provider>
        </CellClickCtx.Provider>
    );
}
